import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import TopicIcon from "@mui/icons-material/Topic";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DescriptionIcon from "@mui/icons-material/Description";

const ViewCallDetailsModal = ({ open, onClose, data }) => {
  if (!data) return null;

  const getOutcomeColor = (outcome) => {
    const colors = {
      Resolved: "success",
      Completed: "success",
      Pending: "warning",
      "Follow-up Required": "warning",
      Transferred: "info",
      Escalated: "error",
      Failed: "error",
    };
    return colors[outcome] || "default";
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "Positive":
        return <SentimentSatisfiedAltIcon sx={{ color: "#4CAF50" }} />;
      case "Neutral":
        return <SentimentNeutralIcon sx={{ color: "#FF9800" }} />;
      case "Negative":
        return <SentimentDissatisfiedIcon sx={{ color: "#F44336" }} />;
      default:
        return <SentimentNeutralIcon sx={{ color: "#FF9800" }} />;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "Positive":
        return { bg: "#E8F5E9", color: "#2E7D32" };
      case "Neutral":
        return { bg: "#FFF3E0", color: "#E65100" };
      case "Negative":
        return { bg: "#FFEBEE", color: "#C62828" };
      default:
        return { bg: "#F5F5F5", color: "#666" };
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const sentimentColors = getSentimentColor(data.sentiment_label);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={600}>
            Call Analysis Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Call Information Section */}
          <Card sx={{ backgroundColor: "#F9FAFB", border: "1px solid #E0E0E0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <PhoneIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Call Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InfoRow label="Call ID" value={data.call_id} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow
                    label="Outcome"
                    value={
                      <Chip
                        label={data.outcome}
                        size="small"
                        color={getOutcomeColor(data.outcome)}
                        sx={{ fontWeight: 500 }}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow
                    label="Duration"
                    value={formatDuration(data.duration_seconds)}
                    icon={<AccessTimeIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow
                    label="Cost"
                    value={`$${data.cost?.toFixed(2)}`}
                    icon={<AttachMoneyIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow
                    label="Customer"
                    value={data.customer_name}
                    icon={<PersonIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Agent" value={data.agent_name} />
                </Grid>
                <Grid item xs={12}>
                  <InfoRow
                    label="Created At"
                    value={data.created_at}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Sentiment Analysis Section */}
          <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <AssessmentIcon color="info" />
                <Typography variant="h6" fontWeight={600}>
                  Sentiment Analysis
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Sentiment:
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      {getSentimentIcon(data.sentiment_label)}
                      <Chip
                        label={data.sentiment_label}
                        size="small"
                        sx={{
                          backgroundColor: sentimentColors.bg,
                          color: sentimentColors.color,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Sentiment Score" value={`${data.sentiment_score}%`} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Topics Section */}
          {data.topics && data.topics.length > 0 && (
            <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <TopicIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Topics Discussed ({data.topics.length})
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {data.topics.map((topic, index) => (
                    <Chip
                      key={index}
                      label={topic}
                      size="small"
                      sx={{
                        backgroundColor: "#E3F2FD",
                        color: "#1565C0",
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Summary Section */}
          {data.summary && (
            <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <DescriptionIcon color="success" />
                  <Typography variant="h6" fontWeight={600}>
                    Summary
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: "#F5F5F5",
                    p: 2,
                    borderRadius: 1,
                    lineHeight: 1.6,
                  }}
                >
                  {data.summary}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Full Analysis Section */}
          {data.full_analysis && (
            <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <AssessmentIcon color="secondary" />
                  <Typography variant="h6" fontWeight={600}>
                    Full Analysis
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: "#F5F5F5",
                    p: 2,
                    borderRadius: 1,
                    lineHeight: 1.6,
                    maxHeight: 300,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {data.full_analysis}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper component for info rows
const InfoRow = ({ label, value, icon }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" gutterBottom>
      {label}:
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {icon}
      <Typography variant="body2" fontWeight={500}>
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

export default ViewCallDetailsModal;
